// packages/core/src/process-swimlane.ts
//
// Pure swimlane PLACEMENT. Consumes a ProcessScaffold (the variant-agnostic
// structural layer — see process-scaffold.ts) and returns a SwimlaneLayout.
// All geometry lives here: lane assignment (column = callee identity),
// serpentine row-merging, branch band-packing, entry-cell reservation, lane
// anchoring fallbacks. Semantic resolution happens in the scaffold, once.
// No DOM, no React, no IO.

import type { QualifiedName } from "./types.js";
import type { WalkResult } from "./process-walker-types.js";
import type {
  Lane,
  Row,
  Frame,
  FrameBranch,
  SwimlaneContext,
  SwimlaneLayout,
  SwimlaneVariant,
} from "./process-swimlane-types.js";
import { buildScaffold } from "./process-scaffold.js";
import type {
  ProcessScaffold,
  ScaffoldBranch,
  ScaffoldNode,
  ScaffoldStep,
} from "./process-scaffold-types.js";

export interface SwimlaneLayoutOpts {
  /**
   * "flow" (default) merges consecutive left-to-right steps onto the
   * same row so a chain `a > b > c > d` shows in a single row.
   * "sequence" emits one row per step like a classic UML sequence
   * diagram.
   */
  readonly variant?: SwimlaneVariant;
}

export function swimlaneLayout(
  walk: WalkResult,
  ctx: SwimlaneContext,
  opts: SwimlaneLayoutOpts = {},
): SwimlaneLayout {
  return swimlaneLayoutFromScaffold(buildScaffold(walk, ctx), opts);
}

export function swimlaneLayoutFromScaffold(
  scaffold: ProcessScaffold,
  opts: SwimlaneLayoutOpts = {},
): SwimlaneLayout {
  const variant: SwimlaneVariant = opts.variant ?? "flow";
  const lanes = buildLanes(scaffold);
  const laneIdByStep = mapStepsToLanes(scaffold.linear);
  const laneIndexById = new Map<string, number>(lanes.map((l) => [l.id, l.index]));
  const { rows, framesByNodeId } = buildRowsAndFrames(
    scaffold,
    laneIdByStep,
    laneIndexById,
    lanes,
    variant,
  );
  const frames = [...framesByNodeId.values()];
  // `height` is the number of distinct grid rows, not the number of step
  // rows. In flow, consecutive LTR steps merge onto a shared `y`, so
  // `rows.length` over-counts — the renderer turns `height` into grid
  // rows (one min-height marker each), and any surplus shows as blank
  // trailing rows below the diagram.
  let maxY = -1;
  for (const r of rows) if (r.y > maxY) maxY = r.y;
  return { lanes, rows, frames, height: maxY + 1, variant };
}

function laneIdFor(
  path: QualifiedName,
  kind: "module" | "actor",
  instance?: string,
): string {
  const base = (kind === "actor" ? "actor:" : "mod:") + path.join(".");
  // v0.9.0 §1.5: a membership instance gets its OWN lane (`mod:A#active`) so two
  // deployments of the same member render as distinct lanes; they keep the
  // member's `qualifiedPath`, so coloring/selection still key on the module.
  return instance ? `${base}#${instance}` : base;
}

function mapStepsToLanes(
  steps: readonly ScaffoldStep[],
): Map<number, { from: string; to: string }> {
  const out = new Map<number, { from: string; to: string }>();
  let prevLaneId: string | null = null;
  for (const step of steps) {
    if (step.noteText !== undefined) {
      // Notes pin to a single lane. If the author named an actor /
      // module, use it; otherwise inherit the previous step's lane so
      // the note reads as "while we're on this lane, do X".
      let laneId: string;
      if (step.from.length > 0) {
        const path = step.fromIsActor ? step.from : step.fromModule ?? step.from;
        laneId = laneIdFor(path, step.fromIsActor ? "actor" : "module", step.fromInstance);
      } else if (prevLaneId) {
        laneId = prevLaneId;
      } else {
        // Pre-first-step note with no actor: synthesize a sentinel lane
        // so the renderer still anchors it somewhere visible.
        laneId = "process:notes";
      }
      out.set(step.stepIndex, { from: laneId, to: laneId });
      prevLaneId = laneId;
      continue;
    }
    const fromPath = step.fromIsActor ? step.from : step.fromModule ?? step.from;
    const fromId = laneIdFor(fromPath, step.fromIsActor ? "actor" : "module", step.fromInstance);
    const toId = laneIdFor(step.toModule, "module", step.toInstance);
    out.set(step.stepIndex, { from: fromId, to: toId });
    prevLaneId = toId;
  }
  return out;
}

interface FrameAccum {
  id: string;
  kind: Frame["kind"];
  label: string;
  branches: FrameBranch[];
  yStart: number;
  yEnd: number;
  laneSpan: { start: number; end: number };
  collapsed: boolean;
  mergeAspect?: string;
  runCount?: number;
}

function buildRowsAndFrames(
  scaffold: ProcessScaffold,
  laneIdByStep: Map<number, { from: string; to: string }>,
  laneIndexById: Map<string, number>,
  lanes: readonly Lane[],
  variant: SwimlaneVariant,
): { rows: Row[]; framesByNodeId: Map<string, Frame> } {
  const rows: Row[] = [];
  const framesByNodeId = new Map<string, Frame>();
  const accum = new Map<string, FrameAccum>();
  const enclosingStack: string[] = [];
  let y = 0;

  function pushFrame(id: string, kind: Frame["kind"], label: string, collapsed: boolean, mergeAspect?: string, runCount?: number) {
    accum.set(id, {
      id,
      kind,
      label,
      branches: [],
      yStart: y,
      yEnd: y - 1,
      laneSpan: { start: Number.POSITIVE_INFINITY, end: Number.NEGATIVE_INFINITY },
      collapsed,
      mergeAspect,
      runCount,
    });
    enclosingStack.push(id);
  }
  function popFrame(id: string) {
    const f = accum.get(id);
    if (!f) return;
    framesByNodeId.set(id, {
      id: id as Frame["id"],
      kind: f.kind,
      label: f.label,
      branches: f.branches,
      yStart: f.yStart,
      yEnd: Math.max(f.yEnd, f.yStart - 1),  // -1 stays when empty
      laneSpan:
        f.laneSpan.start === Number.POSITIVE_INFINITY
          ? { start: 0, end: Math.max(0, laneIndexById.size - 1) }
          : f.laneSpan,
      collapsed: f.collapsed,
      mergeAspect: f.mergeAspect,
      ...(f.runCount !== undefined ? { runCount: f.runCount } : {}),
    });
    enclosingStack.pop();
  }
  function recordRowInFrames(rowY: number, fromLane: string, toLane: string) {
    // Flow renders a block on the CALLEE lane only, so a frame's lane span
    // should cover callees alone. Sequence draws the caller lane too (the
    // arrow originates there), so it spans both. Folding the caller into
    // flow spans would stretch frame rects / rails past any real block.
    const lanes = variant === "flow" ? [toLane] : [fromLane, toLane];
    for (const fid of enclosingStack) {
      const f = accum.get(fid);
      if (!f) continue;
      if (f.yEnd < f.yStart) f.yStart = Math.min(f.yStart, rowY);
      f.yEnd = Math.max(f.yEnd, rowY);
      for (const ln of lanes) {
        const i = laneIndexById.get(ln);
        if (i !== undefined) {
          f.laneSpan.start = Math.min(f.laneSpan.start, i);
          f.laneSpan.end = Math.max(f.laneSpan.end, i);
        }
      }
    }
  }
  // Track the current row as a MONOTONIC run of callee lanes. A row flows
  // in one direction (L→R or R→L); the next step joins it only while that
  // direction holds, otherwise it starts a new row (a "step down"). This
  // gives a serpentine layout — right, down, left, down, right …
  let currentRowHasStep = false;
  let currentRowDir = 0; // -1 = leftward, 0 = undetermined (1 step), +1 = rightward
  let currentRowLastToI = -1; // callee lane of the last step on the row
  // The first real step reserves a whole cell for the start dot, in its
  // CALLER lane on its row. No later step (serpentine reversal, band-packed
  // branch) may land in that cell — so the dot always gets its own slot
  // instead of sharing a node. -1 until set.
  let entryCellY = -1;
  let entryCellLane = -1;
  // While walking an alternative branch of an if/switch/try/parallel, the
  // flat `rows` list also contains sibling branches' rows. A terminator
  // that is the first action in its branch must not borrow a sibling's
  // lane — so we remember where the current branch's own rows begin and
  // the lane control was on when the branch was entered.
  const branchCtxStack: {
    floor: number;
    entryLane: string;
    frameId?: string;
    kind?: Frame["kind"];
    label?: string;
    selected?: boolean;
  }[] = [];
  // Nearest enclosing BRANCHED frame (if/switch/try/parallel), used to stamp
  // branch identity onto every row so the renderer can find each branch's
  // head/tail (it matches rows by branchFrameId+branchLabel) and draw the
  // split→head / tail→merge gateway arrows — and so terminators / subprocess
  // headers, which have no walker step to read modifiers from, land in the
  // correct branch. `parallel` MUST be included: without it, parallel branch
  // steps go unstamped, the gateway can't locate them (so no branch in/out
  // arrows render), and they fall through to the sequential flow-arrow pass —
  // appearing ordered by execution/caller rather than wrapped as branches.
  // `each`/`do` never push a branch context, so they're naturally excluded.
  function currentBranch() {
    for (let i = branchCtxStack.length - 1; i >= 0; i--) {
      const c = branchCtxStack[i]!;
      if (c.kind === "if" || c.kind === "switch" || c.kind === "try" || c.kind === "parallel") return c;
    }
    return undefined;
  }
  function branchStamp(): {
    branchFrameId?: string;
    branchLabel?: string;
    branchSelected?: boolean;
  } {
    const c = currentBranch();
    if (!c) return {};
    // Selected iff every enclosing branch (any kind) is selected — matches
    // the walker's `branchSelected` (AND over all branch ancestors).
    const selected = branchCtxStack.every((e) => e.selected !== false);
    return { branchFrameId: c.frameId, branchLabel: c.label, branchSelected: selected };
  }
  function emitRowForStep(stepIndex: number) {
    const ids = laneIdByStep.get(stepIndex);
    if (!ids) return;
    const step = scaffold.byIndex.get(stepIndex);
    const toI = laneIndexById.get(ids.to) ?? -1;
    // Flow: a step joins the current row if it CONTINUES the row's
    // direction (or sets it, on the 2nd step). A reversal — or no lane
    // progress — starts a new row. So `a > b > c` runs right, then if the
    // next callee is to the left the chain drops a row and runs left,
    // serpentine-style, instead of forcing a fresh row per leftward step.
    // Sequence: never merge — one step per row.
    // Prose renders as a FULL-WIDTH annotation row (§2.6) — it can never
    // share a row with a step, before or after.
    const isProse = step?.prose === true;
    let canShare = false;
    if (variant === "flow" && !isProse && currentRowHasStep && toI >= 0 && currentRowLastToI >= 0) {
      if (currentRowDir === 0) canShare = toI !== currentRowLastToI;
      else if (currentRowDir > 0) canShare = toI > currentRowLastToI;
      else canShare = toI < currentRowLastToI;
    }
    // Never share into the cell reserved for the start dot.
    if (canShare && y === entryCellY && toI === entryCellLane) canShare = false;
    const prevDir = currentRowDir;
    const prevLast = currentRowLastToI;
    if (!canShare) {
      if (currentRowHasStep) y++;
      currentRowDir = 0;
      currentRowLastToI = -1;
    }
    const enclosing = [...enclosingStack];
    const compId = step?.modifiers.compensation ? step.modifiers.reversibleId : undefined;
    rows.push({
      y,
      stepIndex,
      fromLaneId: ids.from,
      toLaneId: ids.to,
      bind: step?.bind,
      args: step?.args,
      ...(step?.comment ? { comment: step.comment } : {}),
      ...(step?.reentryTarget ? { reentry: true } : {}),
      enclosingFrames: enclosing,
      selfCall: ids.from === ids.to,
      ...(compId ? { comp: { reversibleId: compId } } : {}),
      ...branchStamp(),
    });
    recordRowInFrames(y, ids.from, ids.to);
    // The 2nd step on a row fixes its direction.
    if (canShare && prevDir === 0 && toI >= 0 && prevLast >= 0 && toI !== prevLast) {
      currentRowDir = toI > prevLast ? 1 : -1;
    }
    if (isProse) {
      // Close the prose row immediately so the NEXT step starts fresh below
      // it instead of merging beside the full-width annotation.
      y++;
      currentRowHasStep = false;
      currentRowDir = 0;
      currentRowLastToI = -1;
      return;
    }
    currentRowHasStep = true;
    if (toI >= 0) currentRowLastToI = toI;
    // First real step (not a note, distinct caller lane) reserves its
    // caller-lane cell for the start dot.
    if (entryCellY < 0 && variant === "flow" && step?.noteText === undefined) {
      const fromI = laneIndexById.get(ids.from) ?? -1;
      if (fromI >= 0 && fromI !== toI) {
        entryCellY = y;
        entryCellLane = fromI;
      }
    }
  }
  // Every container / branch boundary forces a new row for the next
  // step that follows it (sequence) OR for a hard terminator break
  // (both variants). Flow swallows container boundaries: a step inside
  // an `each`, `try`, or `do` body that's still left-to-right from
  // the prior step's callee should merge naturally onto the same line.
  function endRow() {
    if (variant === "flow") return;
    if (currentRowHasStep) {
      y++;
      currentRowHasStep = false;
      currentRowDir = 0;
      currentRowLastToI = -1;
    }
  }
  function endRowHard() {
    // Used by terminator + cross-branch transitions where we always
    // want a fresh row, even in flow (so alternatives are not
    // visually concatenated into one chain).
    if (currentRowHasStep) {
      y++;
      currentRowHasStep = false;
      currentRowDir = 0;
      currentRowLastToI = -1;
    }
  }

  function emitBranchedFrame(
    id: string,
    kind: Frame["kind"],
    label: string,
    branches: readonly ScaffoldBranch[],
    mergeAspect?: string,
    runCount?: number,
  ): void {
    endRow();
    pushFrame(id, kind, label, false, mergeAspect, runCount);
    // Lane control is on when the frame is entered — the fallback anchor
    // for a terminator that is the first action in any of these branches.
    const frameEntryLane = rows[rows.length - 1]?.toLaneId ?? lanes[0]?.id ?? "";

    if (variant !== "flow") {
      // Sequence: classic UML alt/par layout — one branch after another,
      // each on its own stack of rows separated by a hard row break.
      const accumBranches: FrameBranch[] = [];
      for (const b of branches) {
        endRowHard();
        const before = rows.length;
        branchCtxStack.push({ floor: before, entryLane: frameEntryLane, frameId: id, kind, label: b.label, selected: b.selected });
        walkTree(b.children);
        branchCtxStack.pop();
        endRowHard();
        let bStart = Number.POSITIVE_INFINITY;
        let bEnd = Number.NEGATIVE_INFINITY;
        for (let i = before; i < rows.length; i++) {
          const ry = rows[i]!.y;
          if (ry < bStart) bStart = ry;
          if (ry > bEnd) bEnd = ry;
        }
        const branchStartY = bStart === Number.POSITIVE_INFINITY ? y : bStart;
        const branchEndY =
          bEnd === Number.NEGATIVE_INFINITY ? branchStartY - 1 : bEnd;
        accumBranches.push({
          label: b.label,
          yStart: branchStartY,
          yEnd: branchEndY,
          selected: b.selected,
        });
      }
      const f = accum.get(id);
      if (f) f.branches = accumBranches;
      popFrame(id);
      return;
    }

    // Flow: BPMN-style gateway layout. A SPLIT diamond opens the frame on
    // its own line; the branches band-pack below it (alternatives tile side
    // by side, colliding lanes drop a row); a MERGE diamond closes it on its
    // own line. The chain flows fork-step → split → branch heads, and branch
    // tails → merge → continuation.
    const forkLane = frameEntryLane;
    // SPLIT gateway — own row, in the lane the chain forks from.
    if (currentRowHasStep) y++;
    const splitY = y;
    rows.push({
      y,
      stepIndex: null,
      fromLaneId: forkLane,
      toLaneId: forkLane,
      enclosingFrames: [...enclosingStack],
      selfCall: true,
      gateway: { kind: "split", frameId: id, frameKind: kind },
      ...branchStamp(),
    });
    recordRowInFrames(splitY, forkLane, forkLane);
    y++;
    const branchBaseY = y;
    // Occupied (gridY, laneIndex) cells, for band-packing collisions.
    const occupied = new Set<string>();
    const accumBranches: FrameBranch[] = [];
    let frameMaxY = branchBaseY - 1;
    for (const b of branches) {
      // Re-anchor the chain cursor at the band base (just below the split):
      // each branch opens a fresh band rather than continuing below the
      // previous branch. Branches whose blocks fall in DIFFERENT lanes tile
      // side by side on the SAME row (A B, bracketed by ^split / vmerge);
      // only branches that would collide in a lane drop a row (below). This
      // holds for every branched frame, parallel included — the split/merge
      // diamonds and per-branch in/out arrows convey concurrency, not a
      // one-branch-per-row stack.
      y = branchBaseY;
      currentRowHasStep = false;
      currentRowDir = 0;
      currentRowLastToI = -1;
      const before = rows.length;
      const framesBefore = new Set(framesByNodeId.keys());
      branchCtxStack.push({ floor: before, entryLane: frameEntryLane, frameId: id, kind, label: b.label, selected: b.selected });
      walkTree(b.children);
      branchCtxStack.pop();
      // Find the smallest downward shift that clears every placed cell.
      // delta 0 → a clean side-by-side band.
      let delta = 0;
      for (;;) {
        let clash = false;
        for (let i = before; i < rows.length; i++) {
          const li = laneIndexById.get(rows[i]!.toLaneId);
          if (li === undefined) continue;
          if (occupied.has(`${rows[i]!.y + delta}:${li}`)) {
            clash = true;
            break;
          }
        }
        if (!clash) break;
        delta++;
      }
      if (delta > 0) {
        for (let i = before; i < rows.length; i++) {
          rows[i] = { ...rows[i]!, y: rows[i]!.y + delta };
        }
        // Frames closed during this branch walk live at pre-shift y —
        // move them down with their rows.
        for (const k of framesByNodeId.keys()) {
          if (framesBefore.has(k)) continue;
          const fr = framesByNodeId.get(k)!;
          framesByNodeId.set(k, { ...fr, yStart: fr.yStart + delta, yEnd: fr.yEnd + delta });
        }
      }
      let bStart = Number.POSITIVE_INFINITY;
      let bEnd = Number.NEGATIVE_INFINITY;
      for (let i = before; i < rows.length; i++) {
        const r = rows[i]!;
        const li = laneIndexById.get(r.toLaneId);
        if (li !== undefined) occupied.add(`${r.y}:${li}`);
        if (r.y < bStart) bStart = r.y;
        if (r.y > bEnd) bEnd = r.y;
      }
      const branchStartY = bStart === Number.POSITIVE_INFINITY ? branchBaseY : bStart;
      const branchEndY =
        bEnd === Number.NEGATIVE_INFINITY ? branchStartY - 1 : bEnd;
      accumBranches.push({
        label: b.label,
        yStart: branchStartY,
        yEnd: branchEndY,
        selected: b.selected,
      });
      if (branchEndY > frameMaxY) frameMaxY = branchEndY;
    }
    // MERGE gateway — own row, below the tallest branch band, same lane as
    // the split so the fork/join read as a vertical spine.
    const mergeY = frameMaxY + 1;
    rows.push({
      y: mergeY,
      stepIndex: null,
      fromLaneId: forkLane,
      toLaneId: forkLane,
      enclosingFrames: [...enclosingStack],
      selfCall: true,
      gateway: { kind: "merge", frameId: id, frameKind: kind },
      ...branchStamp(),
    });
    recordRowInFrames(mergeY, forkLane, forkLane);
    // Extend this frame and every enclosing frame to cover split..merge.
    for (const fid of enclosingStack) {
      const fr = accum.get(fid);
      if (fr) fr.yEnd = Math.max(fr.yEnd, mergeY);
    }
    const f = accum.get(id);
    if (f) f.branches = accumBranches;
    popFrame(id);
    // The continuation joins below the merge on a fresh row.
    y = mergeY + 1;
    currentRowHasStep = false;
    currentRowDir = 0;
    currentRowLastToI = -1;
  }

  function walkTree(nodes: readonly ScaffoldNode[]): void {
    for (const n of nodes) {
      if (n.kind === "step") {
        // Steps inside collapsed subprocess are skipped.
        if (n.modifiers.collapsedSubprocessGroup) continue;
        emitRowForStep(n.stepIndex);
      } else if (n.kind === "each") {
        endRow();
        pushFrame(n.nodeId, "each", n.label, false);
        walkTree(n.children);
        endRow();
        popFrame(n.nodeId);
      } else if (n.kind === "branched") {
        emitBranchedFrame(n.nodeId, n.sub, n.label, n.groups, n.mergeAspect, n.runCount);
      } else if (n.kind === "do") {
        // Anchor the subprocess in its FIRST CALLEE's lane (the lane its body
        // opens into) rather than inheriting the preceding step's lane, which
        // is arbitrary — the scaffold resolved that anchor (raw, legacy
        // quirks preserved).
        const lane = n.anchorModule
          ? laneIdFor(n.anchorModule, "module")
          : rows[rows.length - 1]?.toLaneId ?? lanes[0]?.id ?? "";
        const doToI = laneIndexById.get(lane) ?? -1;
        if (n.expanded) {
          // An EXPANDED `do` is a section: header on its own row, body opens
          // beneath it (still enclosed by the `do` frame), and hard breaks
          // bracket the whole thing — neighbours must not merge through an
          // open subprocess as if it weren't there.
          endRowHard();
          pushFrame(n.nodeId, "do", n.label, false);
          rows.push({
            y,
            stepIndex: null,
            fromLaneId: lane,
            toLaneId: lane,
            enclosingFrames: [...enclosingStack],
            selfCall: true,
            subprocessHeader: { nodeId: n.nodeId, label: n.label, expanded: true },
            ...(n.comment ? { comment: n.comment } : {}),
            ...branchStamp(),
          });
          recordRowInFrames(y, lane, lane);
          y++;
          currentRowHasStep = false;
          currentRowDir = 0;
          currentRowLastToI = -1;
          walkTree(n.body!);
          endRowHard();
          popFrame(n.nodeId);
        } else {
          // A COLLAPSED `do` is a single lane-scoped node — it may SAMELINE
          // with neighbouring steps (same row, a different lane) exactly like
          // a step, following the row's left-to-right / serpentine direction.
          // (Mirrors emitRowForStep; flow only — sequence keeps one per row.)
          let canShare = false;
          if (variant === "flow" && currentRowHasStep && doToI >= 0 && currentRowLastToI >= 0) {
            if (currentRowDir === 0) canShare = doToI !== currentRowLastToI;
            else if (currentRowDir > 0) canShare = doToI > currentRowLastToI;
            else canShare = doToI < currentRowLastToI;
          }
          // Never share into the cell reserved for the start dot.
          if (canShare && y === entryCellY && doToI === entryCellLane) canShare = false;
          const prevDir = currentRowDir;
          const prevLast = currentRowLastToI;
          if (!canShare) {
            if (currentRowHasStep) y++;
            currentRowDir = 0;
            currentRowLastToI = -1;
          }
          pushFrame(n.nodeId, "do", n.label, true);
          rows.push({
            y,
            stepIndex: null,
            fromLaneId: lane,
            toLaneId: lane,
            enclosingFrames: [...enclosingStack],
            selfCall: true,
            subprocessHeader: { nodeId: n.nodeId, label: n.label, expanded: false },
            ...(n.comment ? { comment: n.comment } : {}),
            ...branchStamp(),
          });
          recordRowInFrames(y, lane, lane);
          popFrame(n.nodeId);
          // The 2nd cell on a row fixes its direction.
          if (canShare && prevDir === 0 && doToI >= 0 && prevLast >= 0 && doToI !== prevLast) {
            currentRowDir = doToI > prevLast ? 1 : -1;
          }
          currentRowHasStep = true;
          if (doToI >= 0) currentRowLastToI = doToI;
          // No y++ — a following step may sameline onto this row after the `do`.
        }
      } else if (n.kind === "terminator") {
        // Terminator (fail/finish) is a hard control-flow boundary —
        // always force its own row even in flow.
        endRowHard();
        // Anchor in the lane control is currently on. Normally that is
        // the previous row's `to`. But when the terminator is the first
        // action in an alternative branch, `rows[last]` is a sibling
        // branch's row (showAllBranches) — borrowing its lane leaks an
        // unrelated participant. Fall back to the lane active when the
        // branch was entered.
        const bctx = branchCtxStack[branchCtxStack.length - 1];
        const prev = rows[rows.length - 1];
        const lane =
          bctx && rows.length - 1 < bctx.floor
            ? bctx.entryLane
            : prev?.toLaneId ?? lanes[0]?.id ?? "";
        rows.push({
          y,
          stepIndex: null,
          fromLaneId: lane,
          toLaneId: lane,
          enclosingFrames: [...enclosingStack],
          selfCall: true,
          terminator: { type: n.type, reason: n.reason, nodeId: n.nodeId, ...(n.all ? { all: true } : {}) },
          ...(n.comp ? { comp: { reversibleId: n.comp.reversibleId } } : {}),
          ...branchStamp(),
        });
        recordRowInFrames(y, lane, lane);
        y++;
      }
    }
  }

  walkTree(scaffold.tree);
  return { rows, framesByNodeId };
}

function buildLanes(scaffold: ProcessScaffold): Lane[] {
  const lanes: Lane[] = [];
  const seen = new Map<string, Lane>();

  function record(path: QualifiedName, kind: "module" | "actor", instance?: string): void {
    const id = laneIdFor(path, kind, instance);
    if (seen.has(id)) return;
    const lane: Lane = {
      id,
      kind,
      label: instance ?? path[path.length - 1] ?? "",
      qualifiedPath: path,
      index: lanes.length,
    };
    lanes.push(lane);
    seen.set(id, lane);
  }

  for (const step of scaffold.linear) {
    // Caller side.
    if (step.fromIsActor) {
      record(step.from, "actor", step.fromInstance);
    } else if (step.fromModule && step.fromModule.length > 0) {
      record(step.fromModule, "module", step.fromInstance);
    }
    // Callee side.
    if (step.toModule.length > 0) record(step.toModule, "module", step.toInstance);
  }

  return lanes;
}
